import { FC, ReactNode, useState } from 'react';
import { saveAs } from 'file-saver';
import NProgress from 'nprogress';
import classNames from 'classnames';
import Button from 'jbc-front/components/Button';
import { Modal } from 'jbc-front/components/presenters/ui/Modal';
import { exportRegularHealthCheckupResult } from '@/api';
import { isErrorWithMessage } from '@/components/utils/isErrorWithMessage';
import { useNotify } from '@/hooks/useNotify';
import styles from './DownloadModal.scss';

type Option = { value: string; label: string };

interface RadioFieldProps {
  label: string;
  options: Option[];
  state: string;
  setState: (value: string) => void;
  note?: ReactNode;
  radioNote?: ReactNode;
  disabled?: boolean;
}

const RadioField: FC<RadioFieldProps> = ({
  label,
  options,
  state,
  setState,
  note = null,
  radioNote = null,
  disabled = false
}) => (
  <div className={styles.field}>
    <div className={styles.label}>{label}</div>
    <div className={styles.note}>{note}</div>
    <div>
      {options.map(({ label, value }) => {
        return <InputRadio {...{ label, value, disabled, state, setState }} key={value} />;
      })}
    </div>
    {radioNote && <div className={styles.note}>{radioNote}</div>}
  </div>
);

interface InputRadioProps {
  label: string;
  value: string;
  disabled: boolean;
  state: string;
  setState: (value: string) => void;
}

const InputRadio: FC<InputRadioProps> = ({ label, value, disabled, state, setState }) => (
  <div style={{ display: 'inline-block' }} className="input-label" key={value}>
    <label>
      <input
        type="radio"
        value={value}
        checked={value === state}
        disabled={disabled}
        className={classNames('m-radio-input', { disabled })}
        onClick={() => setState(value)}
      />
      <span className={classNames('m-radio-parts', { disabled })}>{label}</span>
    </label>
  </div>
);

const downloadOptions = [
  { value: 'UTF-8', label: 'UTF-8（推奨）' },
  { value: 'Windows-31J', label: 'Shift-JIS' }
];

export const DownloadModal: FC = () => {
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [loading, setLoading] = useState<boolean>(false);

  const handleCloseModal = () => setIsOpen(false);

  const exportResult = async () => {
    NProgress.start();
    setLoading(true);
    try {
      const res = await exportRegularHealthCheckupResult({ encoding });
      saveAs(res.data, decodeURIComponent(res.headers['content-disposition'].match(/filename="(.*)"/)[1]));
      setIsOpen(false);
    } catch (err) {
      let errorMessage: string;
      if (isErrorWithMessage(err, '422')) {
        errorMessage = '使用できない文字が含まれています。';
      } else if (isErrorWithMessage(err, '400')) {
        errorMessage = 'encodingは[UTF-8][Windows-31J]いずれかを設定してください';
      } else {
        errorMessage = '予期せぬエラーが発生しました';
      }
      notify(errorMessage, 'error');
    } finally {
      NProgress.done();
      setLoading(false);
    }
  };

  return (
    <>
      <Button widthAuto onClick={() => setIsOpen(true)}>
        個人結果ダウンロード
      </Button>
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <Modal.Header onClose={handleCloseModal}>個人結果ダウンロード</Modal.Header>
        <Modal.Body>
          <RadioField
            label="ファイル形式選択"
            options={downloadOptions}
            state={encoding}
            setState={setEncoding}
            radioNote="Excel2016より前のバージョンのExcelをお使いの方はShift-JISをご利用ください。"
          />
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button className={styles.footerButton} onClick={handleCloseModal}>
            キャンセル
          </Button>
          <Button className={styles.footerButton} primary onClick={exportResult} disabled={loading}>
            ダウンロード
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
